import { PlusOutlined } from '@ant-design/icons';
import { MoreHorizontal } from 'lucide-react';
import { useContext, useMemo } from 'react';
import { css } from 'styled-components';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import Modal from '@/components/Modal/Modal';
import Tooltip from '@/components/Tooltip/TooltipV2';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState, { type ModalStateInterface } from '@/hooks/pure/useModalState';
import service from '@/services';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { clipboardCopy } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import TaxonomyContext from '../../../context/TaxonomyContext';
import {
  buildTaxonomyFeedbackPath,
  buildTaxonomyTopicPath,
  buildWorkspaceTaxonomyPath,
} from '../../../utils/routes';
import taxonomyUtils from '../../../utils/taxonomy';
import EditTopicModal from '../../TaxonomyDetail/CreateTopicModal/EditTopicModal';
import TaxonomyExportModal from '../../TaxonomyExportModal/TaxonomyExportModal';
import TaxonomySetupModal from '../../TaxonomySetupModal/TaxonomySetupModal';

type Props = {
  level: number;
  topic_alias?: string;
  data?: Topic2T<TOPIC_2_TYPE>;
  taxonomyAlias: string;
  aiSuggestionModalState: ModalStateInterface<undefined>;
  createModalState: ModalStateInterface<undefined>;
  childTopicType: TOPIC_2_TYPE;
  refetch: () => void;
  onViewInsightsClick: () => void;
};

const HeaderActions = (props: Props) => {
  const { fetchTopics, taxonomy } = useContext(TaxonomyContext);
  const { isEditor } = useUserRole();
  const addToast = useToast();
  const setPopup = usePopup();
  const {
    data,
    topic_alias,
    taxonomyAlias,
    aiSuggestionModalState,
    createModalState,
    childTopicType,
    refetch,
  } = props;
  const exportModalState = useModalState();
  const viewSettingModalState = useModalState();
  const wr = useWorkspaceRouter();

  const menuOptions = useMemo(() => {
    if (topic_alias)
      return [
        // {
        //   label: 'Duplicate',
        //   onClick: () => {},
        // },
        {
          label: 'Settings',
          onClick: () => viewSettingModalState.open(),
        },
        {
          label: 'Share',
          onClick: () => {
            const wa = getCurrWorkspaceAlias();
            clipboardCopy(
              `${window.location.origin}${buildWorkspaceTaxonomyPath(
                wa,
                buildTaxonomyTopicPath(taxonomyAlias, topic_alias),
              )}`,
            );
            addToast('Topic URL copied', 'success');
          },
        },
        {
          label: 'Export',
          onClick: () => exportModalState.open(),
          disabled: !isEditor,
        },
        {
          label: 'Delete',
          onClick: () => {
            setPopup({
              popupTheme: 'negative',
              type: 'confirm',
              title: 'Delete topic',
              message: 'Are you sure you want to delete this topic?',
              confirmText: 'Delete',
              cancelText: 'Cancel',
              onConfirm: async () => {
                await service.taxonomy.deleteTopic(topic_alias);
                addToast('Topic deleted', 'success');
                await fetchTopics();
                wr.push(buildTaxonomyFeedbackPath(taxonomyAlias));
              },
            });
          },
        },
      ];
    return [
      // {
      //   label: 'Duplicate',
      //   onClick: () => {},
      // },
      {
        label: 'Settings',
        onClick: () => viewSettingModalState.open(),
      },
      {
        label: 'Share',
        onClick: () => {
          const wa = getCurrWorkspaceAlias();
          clipboardCopy(
            `${window.location.origin}${buildWorkspaceTaxonomyPath(
              wa,
              buildTaxonomyFeedbackPath(taxonomyAlias),
            )}`,
          );
          addToast('Taxonomy URL copied', 'success');
        },
      },
      {
        label: 'Export',
        onClick: () => exportModalState.open(),
        disabled: !isEditor,
      },
    ];
  }, [
    topic_alias,
    viewSettingModalState,
    exportModalState,
    isEditor,
    taxonomyAlias,
    addToast,
    setPopup,
    wr,
    fetchTopics,
  ]);

  const createOptions = useMemo(() => {
    return [
      {
        label: 'Explore with AI',
        onClick: () => aiSuggestionModalState.open(),
        disabled:
          childTopicType === TOPIC_2_TYPE.MANUAL ||
          childTopicType === TOPIC_2_TYPE.RULE ||
          !taxonomyUtils.canCreateChildTopic(data?.level),
      },
      {
        label: 'Create your own',
        onClick: () => createModalState.open(),
        disabled: !taxonomyUtils.canCreateChildTopic(data?.level),
      },
    ];
  }, [aiSuggestionModalState, childTopicType, data?.level, createModalState]);

  const isSavedTopic = data?.category === TOPIC_2_TYPE.SAVED_TOPIC;
  const showViewInsightsButton = Boolean(topic_alias);

  return (
    <Flex g={8} a="center" j="flex-end">
      <Flex g={8}>
        {!isSavedTopic && (
          <Tooltip
            tooltipNode={
              !taxonomyUtils.canCreateChildTopic(props.level)
                ? 'You cannot create more than 4 levels.'
                : null
            }
          >
            <Flex>
              <Menu
                triggerNode={
                  <IconButton
                    disabled={props.level >= 4}
                    icon={<PlusOutlined />}
                    sizeType="m"
                    css={css`
                      background-color: ${(props) =>
                        props.theme.colors.gray100};
                    `}
                    tooltip="Add subtopic"
                  />
                }
                options={createOptions}
              />
            </Flex>
          </Tooltip>
        )}
        {showViewInsightsButton && (
          <Button
            variant="primary"
            sizeType="m"
            onClick={props.onViewInsightsClick}
            disabled={!topic_alias}
          >
            View insights
          </Button>
        )}
        <Menu
          triggerNode={
            <Button
              leftIcon={<MoreHorizontal />}
              sizeType="m"
              variant="ghost"
              edgeAlign="right"
            />
          }
          options={menuOptions}
        />
      </Flex>
      <Modal
        status={exportModalState.status}
        closeModal={exportModalState.close}
      >
        <TaxonomyExportModal
          taxonomyAlias={taxonomyAlias}
          topicAlias={topic_alias}
          title={data?.name || taxonomy?.title || ''}
        />
      </Modal>

      {topic_alias && data && (
        <Modal
          status={viewSettingModalState.status}
          closeModal={viewSettingModalState.close}
        >
          <EditTopicModal
            alias={topic_alias}
            refetch={() => {
              refetch();
              fetchTopics();
            }}
            taxonomyAlias={taxonomyAlias}
          />
        </Modal>
      )}

      {!topic_alias && (
        <Modal
          status={viewSettingModalState.status}
          closeModal={viewSettingModalState.close}
        >
          <TaxonomySetupModal taxonomyAlias={taxonomyAlias} mode="EDIT" />
        </Modal>
      )}
    </Flex>
  );
};

export default HeaderActions;
